/**
 * Service OIDC standalone basé sur oidc-client-ts.
 * Remplace vuex-oidc pour compatibilité Vue 3.
 *
 * Ce service est un singleton qui gère le UserManager
 * et expose les méthodes d'authentification.
 */
import { UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { oidcSettings } from '@/config/oidc'
import logger from '@/utils/logger'

// ── Conversion config vuex-oidc → oidc-client-ts ─────────────────────
const userManagerSettings = {
  authority: oidcSettings.authority,
  client_id: oidcSettings.client_id,
  redirect_uri: oidcSettings.redirect_uri,
  response_type: oidcSettings.response_type,
  scope: oidcSettings.scope,
  automaticSilentRenew: oidcSettings.automaticSilentRenew,
  accessTokenExpiringNotificationTimeInSeconds: oidcSettings.accessTokenExpiringNotificationTime,
  silent_redirect_uri: oidcSettings.silent_redirect_uri,
  post_logout_redirect_uri: oidcSettings.post_logout_redirect_uri,
  filterProtocolClaims: oidcSettings.filterProtocolClaims,
  loadUserInfo: oidcSettings.loadUserInfo,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  metadata: oidcSettings.metadata,
}

const userManager = new UserManager(userManagerSettings)

// ── État réactif (sera lu par le module Vuex) ────────────────────────
let _user = null
let _isAuthenticated = false
let _authenticationIsChecked = false

// ── Callbacks (seront définis par le module Vuex) ────────────────────
let _onUserLoaded = null
let _onUserUnloaded = null
let _onAccessTokenExpiring = null
let _onAccessTokenExpired = null

// ── Events oidc-client-ts ────────────────────────────────────────────
userManager.events.addUserLoaded((user) => {
  _user = user
  _isAuthenticated = true
  _authenticationIsChecked = true
  if (_onUserLoaded) _onUserLoaded(user)
})

userManager.events.addUserUnloaded(() => {
  _user = null
  _isAuthenticated = false
  if (_onUserUnloaded) _onUserUnloaded()
})

userManager.events.addAccessTokenExpiring(() => {
  if (_onAccessTokenExpiring) _onAccessTokenExpiring()
})

userManager.events.addAccessTokenExpired(() => {
  if (_onAccessTokenExpired) _onAccessTokenExpired()
})

userManager.events.addSilentRenewError(() => {
  logger.info('OIDC silent renew error')
})

// ── API publique ─────────────────────────────────────────────────────
const oidcService = {
  /**
   * Initialise le service en chargeant l'utilisateur depuis le storage.
   */
  async init () {
    try {
      const user = await userManager.getUser()
      if (user && !user.expired) {
        _user = user
        _isAuthenticated = true
        if (_onUserLoaded) _onUserLoaded(user)
      }
      _authenticationIsChecked = true
    } catch (error) {
      logger.info('OIDC init error:', error)
      _authenticationIsChecked = true
    }
  },

  /**
   * Redirige vers le fournisseur OIDC pour l'authentification.
   */
  async signIn () {
    return userManager.signinRedirect()
  },

  /**
   * Traite le callback après authentification.
   * @returns {string} Le chemin de redirection
   */
  async signInCallback () {
    const user = await userManager.signinRedirectCallback()
    _user = user
    _isAuthenticated = true
    _authenticationIsChecked = true
    if (_onUserLoaded) _onUserLoaded(user)
    // Retourne le state (redirect path) ou '/'
    return (user.state && user.state.redirectPath) || '/'
  },

  /**
   * Renouvellement silencieux du token.
   */
  async signInSilent () {
    try {
      const user = await userManager.signinSilent()
      _user = user
      _isAuthenticated = true
      if (_onUserLoaded) _onUserLoaded(user)
      return user
    } catch (error) {
      logger.info('OIDC silent sign-in error:', error)
      throw error
    }
  },

  /**
   * Traite le callback du silent renew (iframe).
   */
  async signInSilentCallback () {
    return userManager.signinSilentCallback()
  },

  /**
   * Déconnexion OIDC.
   */
  async signOut () {
    return userManager.signoutRedirect()
  },

  // ── Getters ──────────────────────────────────────────────────────
  get user () { return _user },
  get isAuthenticated () { return _isAuthenticated },
  get authenticationIsChecked () { return _authenticationIsChecked },
  get accessToken () { return _user ? _user.access_token : null },
  get idToken () { return _user ? _user.id_token : null },
  get idTokenExp () { return _user ? _user.expires_at : null },
  get profile () { return _user ? _user.profile : null },

  // ── Enregistrement des callbacks ─────────────────────────────────
  onUserLoaded (cb) { _onUserLoaded = cb },
  onUserUnloaded (cb) { _onUserUnloaded = cb },
  onAccessTokenExpiring (cb) { _onAccessTokenExpiring = cb },
  onAccessTokenExpired (cb) { _onAccessTokenExpired = cb },
}

export default oidcService
